import { useEffect, useRef, useState } from 'react';

import { Icon } from '../ui/Icon.js';
import { useTheme, type Theme } from '../../theme/ThemeProvider.js';
import { ACCENTS } from '../../theme/accents.js';

const APPEARANCE_OPTIONS: { value: Theme; label: string; icon: 'sun' | 'moon' | 'palette' }[] = [
  { value: 'light', label: 'Light', icon: 'sun' },
  { value: 'dark', label: 'Dark', icon: 'moon' },
  { value: 'auto', label: 'Auto', icon: 'palette' },
];

/**
 * ClickUp-style appearance + accent-colour picker. A single top-bar button opens a popover
 * with Light/Dark/Auto appearance tiles and the accent-colour grid. Selections apply live
 * and are persisted per member by the ThemeProvider.
 */
export function ThemePicker(): JSX.Element {
  const { theme, accent, isDark, setTheme, setAccent } = useTheme();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
        title="Appearance & theme"
        aria-label="Appearance & theme"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Icon name={isDark ? 'moon' : 'sun'} className="h-5 w-5" />
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Appearance and theme"
          className="absolute right-0 z-40 mt-2 w-72 rounded-xl border border-slate-200 bg-white p-4 shadow-lg"
        >
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Appearance</p>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {APPEARANCE_OPTIONS.map((option) => {
              const active = theme === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setTheme(option.value)}
                  className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-xs font-medium transition ${
                    active
                      ? 'border-brand-500 bg-brand-50 text-brand-700'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                  aria-pressed={active}
                >
                  <Icon name={option.icon} className="h-4 w-4" />
                  {option.label}
                </button>
              );
            })}
          </div>

          <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-400">Theme</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {ACCENTS.map((option) => {
              const active = accent === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setAccent(option.id)}
                  className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-sm font-medium transition ${
                    active
                      ? 'border-brand-500 bg-brand-50 text-brand-700'
                      : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                  aria-pressed={active}
                >
                  <span
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-white"
                    style={{ backgroundColor: option.swatch }}
                  >
                    {active ? <Icon name="approvals" className="h-3.5 w-3.5" /> : null}
                  </span>
                  <span className="truncate">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
